import { BlobItem, BlobServiceClient } from '@azure/storage-blob';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ModelCampaignMetaData } from '../../models/azure-storage/azure-storage-blob-metadata.model';
import { ModelCampaignListDetails } from '../../models/campaign/campaign-list-details.model';
import { ModelCampaign } from '../../models/campaign/campaign.model';
import { ModelRedirectCampaign } from '../../models/campaign/redirect-campaign.model';
import { AbstractServiceAzureStorage } from './azure-storage.service.abstract';
import { ModelMenu } from '../../models/menu/menu.model';
import { ModelAzureStorageFileUploadResponse } from '../../models/azure-storage/azure-storage-file-upload-response.model';
import { ModelSubscriptionRecord } from '../../models/subscription/subscription-record.model';
import {
  MOCK_BLOB_FILES,
  MOCK_CAMPAIGN_LISTS,
  MOCK_SUBSCRIPTION_RECORDS
} from '../../mock-data/azure-storage.mock-data';

/**
 * In-memory mock of ServiceAzureStorage. The real service uses the @azure/storage-blob SDK directly,
 * which json-server cannot serve, so this mock reads campaign/menu/redirect/subscription data straight
 * from the seed module (src/app/mock-data/azure-storage.mock-data.ts). Write operations succeed as no-ops.
 */
export class MockServiceAzureStorage implements AbstractServiceAzureStorage {
  private readonly STORAGE_BASE = 'https://staatodev001.blob.core.windows.net/campaigns';

  // Runtime-appended subscriptions per directory (`${organizationId}/${campaignId}`).
  private readonly appendedSubscriptions = new Map<string, ModelSubscriptionRecord[]>();

  addCampaignToMasterList(campaignMetaData: ModelCampaignMetaData): void {
    const organizationId = campaignMetaData['organizationid'];
    const list = MOCK_CAMPAIGN_LISTS[organizationId] ?? (MOCK_CAMPAIGN_LISTS[organizationId] = []);
    if (list.some(campaign => campaign.id === campaignMetaData.campaignid)) {
      return;
    }
    list.push({
      id: campaignMetaData.campaignid,
      name: campaignMetaData.name,
      url: campaignMetaData.url,
      description: campaignMetaData.description,
      fromDate: new Date(campaignMetaData.validfrom),
      toDate: new Date(campaignMetaData.validto),
      viewer: campaignMetaData.viewer,
      createdBy: campaignMetaData.uploadedBy,
      blobItems: [],
      isArchived: false
    });
  }

  appendSubscription(directory: string, record: ModelSubscriptionRecord): Promise<void> {
    const key = directory.replace(/\/subscriptions\.json$/, '');
    const records = this.appendedSubscriptions.get(key) ?? [];
    records.push(record);
    this.appendedSubscriptions.set(key, records);
    return Promise.resolve();
  }

  createSubscriptionFile(): Promise<boolean> {
    // false == success (see abstract remarks).
    return Promise.resolve(false);
  }

  async filterBlobsByMetadata(
    organizationId: string,
    filters: { startDate?: Date; toDate?: Date; name?: string }
  ): Promise<ModelCampaignListDetails[]> {
    const campaigns = await this.listCampaignsForOrganization(organizationId);
    return campaigns.filter(campaign => {
      const matchesName = !filters.name || campaign.name.toLowerCase().includes(filters.name.toLowerCase());
      const matchesStartDate = !filters.startDate || campaign.fromDate >= filters.startDate;
      return matchesName && matchesStartDate;
    });
  }

  getBlobItemAbsoluteURL(item: BlobItem): string {
    return `${this.STORAGE_BASE}/${item.name}`;
  }

  getBlobItemFileNameFromAbsoluteURL(url: string): string {
    return url.replace(`${this.STORAGE_BASE}/`, '');
  }

  initializeBlobStorageCRUDClient(): Observable<BlobServiceClient> {
    // No real blob client in mock mode; upload/append paths are overridden to not use it.
    return of({} as unknown as BlobServiceClient);
  }

  listCampaignsForOrganization(organizationId: string): Promise<ModelCampaignListDetails[]> {
    return Promise.resolve(MOCK_CAMPAIGN_LISTS[organizationId] ?? []);
  }

  loadCampaignFile(organizationId: string, campaignId: string): Observable<ModelCampaign> {
    return this.loadFile<ModelCampaign>(`${organizationId}/${campaignId}/campaign.json`);
  }

  loadCampaignSubscriptionFile(organizationId: string, campaignId: string): Observable<ModelSubscriptionRecord[]> {
    const key = `${organizationId}/${campaignId}`;
    const seeded = MOCK_SUBSCRIPTION_RECORDS[key] ?? [];
    const appended = this.appendedSubscriptions.get(key) ?? [];
    // delay(0) mimics real HTTP latency so consumers' view children are initialized before the emission.
    return of([...seeded, ...appended]).pipe(delay(0));
  }

  loadFile<T>(fileName: string): Observable<T> {
    const file = MOCK_BLOB_FILES[fileName];
    if (file === undefined) {
      return throwError(() => new Error(`Mock blob not found: ${fileName}`)).pipe(delay(0));
    }
    // delay(0) mimics real HTTP latency so consumers' view children are initialized before the emission.
    return of(file as T).pipe(delay(0));
  }

  loadMenuFile(organizationId: string, campaignId: string): Observable<ModelMenu> {
    return this.loadFile<ModelMenu>(`${organizationId}/${campaignId}/menu.json`);
  }

  loadOrganizationSubscriptionFile(): Observable<PushSubscription[]> {
    return of([]).pipe(delay(0));
  }

  loadRedirectCampaignFile(organizationId: string, campaignId: string): Observable<ModelRedirectCampaign> {
    return this.loadFile<ModelRedirectCampaign>(`${organizationId}/${campaignId}/redirect-campaign.json`);
  }

  uploadFilesParallel(
    files: { filename: string; file: File; metadata: Record<string, string> }[]
  ): Promise<ModelAzureStorageFileUploadResponse> {
    return Promise.resolve({ successful: files.map(file => file.filename), failed: [] });
  }
}
